import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Send, CheckCircle } from '@mui/icons-material';
import API_CONFIG from '../../config/api';
import TermsAndPrivacyModal from './TermsAndPrivacyModal';

const StickyDemoForm = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: '',
    marketingConsent: false,
    termsConsent: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'marketingConsent' || name === 'termsConsent') ? checked : value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = API_CONFIG.buildUrl('/demo/request');
      console.log('📤 Submitting demo request to:', url);
      console.log('📤 API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          role: formData.role,
          message: formData.message,
          marketing_consent: formData.marketingConsent,
          terms_consent: formData.termsConsent,
        }),
      });
      
      console.log('📥 Response status:', response.status, response.statusText);
      console.log('📥 Response URL:', response.url);

      // Handle different response statuses
      if (response.status === 429) {
        // Rate limit exceeded
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Too many requests. Please wait a moment before trying again, or contact us directly at admin@stratovai.ai');
        return;
      }

      if (!response.ok) {
        // Handle other HTTP errors
        const data = await response.json().catch(() => ({}));
        setError(data.detail || data.message || `Request failed (${response.status}). Please try again or email us at admin@stratovai.ai`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          role: '',
          message: '',
          marketingConsent: false,
          termsConsent: false,
        });
        // Scroll to success message for better UX
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        setError(data.message || data.detail || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      // Network errors or JSON parsing errors
      console.error('Form submission error:', err);
      setError('Network error. Please check your connection and try again, or email us directly at admin@stratovai.ai');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          position: 'sticky',
          top: 90,
          borderRadius: theme.shape.borderRadius * 2,
          border: `2px solid ${theme.palette.success.main}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: '4rem', color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: theme.palette.success.main }}>
            Thank You!
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
            We've received your request and will contact you within 24 hours.
          </Typography>
          <Button
            variant="text"
            onClick={() => setSubmitted(false)}
            sx={{ color: theme.palette.primary.main, textTransform: 'none' }}
          >
            Submit Another Request
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: theme.shape.borderRadius * 2,
        border: `2px solid ${theme.palette.primary.main}`,
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 1,
          color: theme.palette.text.primary,
          textAlign: 'center',
        }}
      >
        Request Early Access
      </Typography>
      
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          mb: 3,
          textAlign: 'center',
        }}
      >
        Get a personalized demo of Stratovai
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
          <TextField
            required
            size="small"
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            variant="outlined"
            error={!!error && !formData.name}
            aria-label="Full Name (required)"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:focus-within': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              },
            }}
          />
          
          <TextField
            required
            size="small"
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            variant="outlined"
            error={!!error && !formData.email}
            aria-label="Email Address (required)"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:focus-within': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              },
            }}
          />
          
          <TextField
            size="small"
            label="Company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            variant="outlined"
            aria-label="Company (optional)"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:focus-within': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              },
            }}
          />
          
          <TextField
            size="small"
            label="Message (Optional)"
            name="message"
            value={formData.message}
            onChange={handleChange}
            variant="outlined"
            multiline
            rows={3}
            placeholder="Any specific time preferences or questions?"
            aria-label="Message (optional)"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:focus-within': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              },
            }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                name="termsConsent"
                checked={formData.termsConsent}
                onChange={handleChange}
                aria-label="I agree to the Terms & Conditions and Privacy Policy (required)"
                sx={{
                  color: theme.palette.primary.main,
                  '&.Mui-checked': {
                    color: theme.palette.primary.main,
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                    borderRadius: '4px',
                  },
                }}
              />
            }
            required={false}
            label={
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary, 
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '2px'
                }}
              >
                <span>I agree to the{' '}</span>
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => setTermsModalOpen(true)}
                  aria-label="Open Terms & Conditions and Privacy Policy"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: '2px',
                      borderRadius: '2px',
                    }
                  }}
                >
                  Terms & Conditions and Privacy Policy
                </Link>
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
            }
          />
          
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleChange}
                aria-label="I agree to receive product updates from Stratovai"
                sx={{
                  color: theme.palette.primary.main,
                  '&.Mui-checked': {
                    color: theme.palette.primary.main,
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                    borderRadius: '4px',
                  },
                }}
              />
            }
            required={false}
            label={
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                I agree to receive product updates from Stratovai
              </Typography>
            }
          />
          
          {error && (
            <Alert 
              severity="error" 
              role="alert"
              aria-live="polite"
              sx={{ 
                py: 1,
                '& .MuiAlert-message': {
                  color: theme.palette.error.dark,
                }
              }}
            >
              {error}
            </Alert>
          )}
          
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.termsConsent}
            startIcon={loading ? <CircularProgress size={16} /> : <Send />}
            aria-label={loading ? 'Submitting demo request' : 'Submit demo request'}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              py: 1.2,
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              width: 'fit-content',
              minWidth: '240px',
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '& .MuiButton-startIcon': {
                margin: 0,
                marginRight: '8px',
              },
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&:focus-visible': {
                outline: `3px solid ${theme.palette.primary.main}`,
                outlineOffset: '2px',
                bgcolor: theme.palette.primary.dark,
              },
              '&:disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              }
            }}
          >
            {loading ? 'Submitting...' : 'Get Personalized Demo'}
          </Button>
        </Box>
      </form>
      
      <TermsAndPrivacyModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
    </Paper>
  );
};

export default StickyDemoForm;
